import { useState } from "react";
import { formatDateInTz } from "@/design-system/lib/format";
import {
  commissionModelerApi,
  type BrokerCashAtClosingMode,
  type CommissionModelDto,
  type CommissionModelResult,
  type CommissionModelTerms,
} from "../../api/commissionModeler";
import {
  cashAtClosingDisplay,
  errorMessage,
  formatMoney,
  formatPercent,
  monthlyFeeDisplay,
} from "./copy";

// Broker Commission Modeler — the workup.
//
// Every number on this surface arrives from the server. Nothing here multiplies,
// divides or accumulates: the one place that could be mistaken for arithmetic is
// the reconciliation block, and even that ADDS NOTHING — it lays the server's
// three monthly figures out as `seller share + spread = buyer payment` so the
// reader can check the identity themselves rather than being told it holds.
//
// That identity is the whole reason the modeler exists. A broker's ongoing
// commission is a percentage of the SELLER'S monthly share, not of the buyer's
// payment — the two differ by Phoenix's retained servicing spread, and at the
// standard terms mistaking one for the other overstates the commission by about
// 1.46x. Showing all three, adding up, is how a broker sees which one they are
// being paid on.

/** Read-only program terms. Phoenix-set — a broker cannot edit these, and the
 *  panel deliberately offers no affordance suggesting otherwise. */
export function TermsPanel({ terms }: { terms: CommissionModelTerms }) {
  return (
    <div data-testid="commission-model-terms">
      <h4 className="m-0 font-display text-caption uppercase tracking-label text-gray-mid">
        Program terms
      </h4>
      <dl className="mt-space-2 m-0 grid grid-cols-2 gap-x-space-3 gap-y-space-1 text-small">
        <dt className="m-0 text-gray-mid">Buyer&rsquo;s rate</dt>
        <dd className="m-0 text-right text-onyx">{formatPercent(terms.interestRate)}</dd>
        <dt className="m-0 text-gray-mid">Seller&rsquo;s rate</dt>
        <dd className="m-0 text-right text-onyx">{formatPercent(terms.sellerInterestRate)}</dd>
        <dt className="m-0 text-gray-mid">Amortization</dt>
        <dd className="m-0 text-right text-onyx">{terms.amortizationMonths} months</dd>
        <dt className="m-0 text-gray-mid">Modeled through</dt>
        <dd className="m-0 text-right text-onyx">month {terms.liquidityEventMonth}</dd>
      </dl>
      <p className="mt-space-2 mb-0 text-caption text-gray-mid">
        Set by Phoenix — shown so you can see what the figures are built on.
      </p>
    </div>
  );
}

function Figure({
  label,
  value,
  note,
  emphasis,
}: {
  label: string;
  value: string;
  note?: string;
  emphasis?: boolean;
}) {
  return (
    <div>
      <dt className="m-0 font-display text-caption uppercase tracking-label text-gray-mid">
        {label}
      </dt>
      <dd
        className={
          emphasis
            ? "m-0 mt-[2px] font-display text-h4 font-bold text-onyx"
            : "m-0 mt-[2px] text-body text-onyx"
        }
      >
        {value}
      </dd>
      {note && <p className="m-0 mt-space-1 text-caption text-gray-mid">{note}</p>}
    </div>
  );
}

/** The three monthly figures, laid out so the identity is visible rather than
 *  asserted. Nothing is computed — the operators are typography. */
function MonthlyReconciliation({ result }: { result: CommissionModelResult }) {
  return (
    <div
      data-testid="commission-model-reconciliation"
      className="rounded-md border border-border-light bg-white-warm p-space-3"
    >
      <dl className="m-0 grid grid-cols-[auto_1fr_auto] items-baseline gap-x-space-2 gap-y-space-1 text-small">
        <span aria-hidden className="text-gray-mid" />
        <dt className="m-0 text-gray-mid">Seller&rsquo;s monthly share</dt>
        <dd className="m-0 text-right tabular-nums text-onyx">
          {formatMoney(result.sellerReceipt)}
        </dd>

        <span aria-hidden className="text-gray-mid">
          +
        </span>
        <dt className="m-0 text-gray-mid">Phoenix servicing spread</dt>
        <dd className="m-0 text-right tabular-nums text-onyx">
          {formatMoney(result.phoenixSpread)}
        </dd>

        <span aria-hidden className="border-t border-border-light pt-space-1 text-gray-mid">
          =
        </span>
        <dt className="m-0 border-t border-border-light pt-space-1 font-semibold text-onyx">
          Buyer&rsquo;s monthly payment
        </dt>
        <dd className="m-0 border-t border-border-light pt-space-1 text-right font-semibold tabular-nums text-onyx">
          {formatMoney(result.buyerMonthlyPayment)}
        </dd>
      </dl>
      <p className="m-0 mt-space-2 text-caption text-gray-mid">
        Your ongoing fee comes out of the seller&rsquo;s share — the top line, not
        the bottom one.
      </p>
    </div>
  );
}

/** The headline block. `compact` drops the commission-component breakdown so the
 *  same component can sit in the form modal's preview rail.
 *
 *  The two cash-at-closing members are the stored INPUTS, and they are optional
 *  because the only Figure that reads them lives in the breakdown `compact`
 *  drops — the form modal's rail has no cash row to describe, so it passes
 *  neither. Absent, the row renders the bare resolved dollar, which is exactly
 *  what it rendered before the mode existed. */
export function HeadlineFigures({
  result,
  compact,
  cashAtClosingMode,
  cashAtClosingPercent,
}: {
  result: CommissionModelResult;
  compact?: boolean;
  cashAtClosingMode?: BrokerCashAtClosingMode;
  cashAtClosingPercent?: number | null;
}) {
  return (
    <div className="flex flex-col gap-space-4" data-testid="commission-model-headline">
      <dl className="m-0 grid grid-cols-1 gap-space-3 sm:grid-cols-2">
        <Figure
          label="What you'd earn in total"
          value={formatMoney(result.lifetimeTotal)}
          note={`Cash at closing and ${result.horizonMonths} months of fee, combined.`}
          emphasis
        />
        <Figure
          label="Note financed"
          value={formatMoney(result.note)}
          note="Total deal size less the down payment."
        />
      </dl>

      <MonthlyReconciliation result={result} />

      {!compact && (
        <dl className="m-0 grid grid-cols-1 gap-space-3 sm:grid-cols-2">
          <Figure
            label="Cash at closing"
            value={cashAtClosingDisplay(
              cashAtClosingMode,
              cashAtClosingPercent,
              result.brokerCashAtClosing,
            )}
            note="Paid directly seller → broker."
          />
          <Figure
            label="Ongoing fee"
            value={formatMoney(result.ongoingTotal)}
            note={`${formatMoney(result.brokerMonthlyCommission)}/month for ${result.horizonMonths} months.`}
          />
        </dl>
      )}
    </div>
  );
}

function ScheduleTable({ result }: { result: CommissionModelResult }) {
  const periodNoun = result.granularity === "Yearly" ? "Year" : "Month";
  return (
    <section aria-label="Schedule" className="flex flex-col gap-space-2">
      <div className="flex flex-wrap items-baseline justify-between gap-space-2">
        <h3 className="m-0 font-display text-lead font-bold text-onyx">Schedule</h3>
        <p className="m-0 text-caption text-gray-mid">
          {result.granularity === "Yearly"
            ? "Grouped by year — payments, principal, interest and fee are the year's totals; balances are where the year ended."
            : "Month by month, to the cent."}
        </p>
      </div>
      {/* Wide content scrolls inside its own container so the page never does. */}
      <div className="overflow-x-auto rounded-lg border border-border-light bg-white-pure">
        <table className="w-full min-w-[52rem] border-collapse text-small">
          {/* Two-tier header. The three seller columns are grouped under one
              spanning cell because they are ONE fact decomposed —
              principal + interest = receipt on every row — while the note's
              balance is a different instrument's number that happens to sit
              alongside. The group header is what stops a reader treating all
              four as one running account. */}
          <thead>
            <tr className="border-b border-border-light bg-white-warm text-left">
              <th scope="col" rowSpan={2} className="px-space-3 py-space-2 align-bottom font-semibold text-gray-mid">
                {periodNoun}
              </th>
              <th
                scope="colgroup"
                colSpan={3}
                data-testid="commission-model-seller-group"
                className="border-l border-border-light px-space-3 py-space-1 text-center font-semibold text-onyx"
              >
                Seller receives
              </th>
              <th scope="col" rowSpan={2} className="border-l border-border-light px-space-3 py-space-2 text-right align-bottom font-semibold text-gray-mid">
                Your fee
              </th>
              <th scope="col" rowSpan={2} className="px-space-3 py-space-2 text-right align-bottom font-semibold text-gray-mid">
                Your fee to date
              </th>
              {/* Separated deliberately: a different instrument's balance, on a
                  different rate. See the divergence note below the table. */}
              <th scope="col" rowSpan={2} className="border-l-2 border-border-light px-space-3 py-space-2 text-right align-bottom font-semibold text-gray-mid">
                Outstanding principal
                <span className="block font-normal normal-case">on the note</span>
              </th>
            </tr>
            <tr className="border-b border-border-light bg-white-warm text-left">
              <th scope="col" className="border-l border-border-light px-space-3 py-space-2 text-right font-normal text-gray-mid">
                Principal
              </th>
              <th scope="col" className="px-space-3 py-space-2 text-right font-normal text-gray-mid">
                Interest
              </th>
              <th scope="col" className="px-space-3 py-space-2 text-right font-semibold text-onyx">
                Receipt
              </th>
            </tr>
          </thead>
          <tbody>
            {result.schedule.map((row) => (
              <tr
                key={row.period}
                className="border-b border-border-light last:border-0 odd:bg-white-warm/40"
              >
                <th
                  scope="row"
                  className="px-space-3 py-space-2 text-left font-normal text-onyx"
                >
                  {row.period}
                </th>
                <td className="border-l border-border-light px-space-3 py-space-2 text-right tabular-nums text-gray-mid">
                  {formatMoney(row.sellerPrincipal)}
                </td>
                <td className="px-space-3 py-space-2 text-right tabular-nums text-gray-mid">
                  {formatMoney(row.sellerInterest)}
                </td>
                <td className="px-space-3 py-space-2 text-right font-semibold tabular-nums text-onyx">
                  {formatMoney(row.sellerReceipt)}
                </td>
                <td className="border-l border-border-light px-space-3 py-space-2 text-right tabular-nums text-onyx">
                  {formatMoney(row.brokerCommission)}
                </td>
                <td className="px-space-3 py-space-2 text-right font-semibold tabular-nums text-onyx">
                  {formatMoney(row.cumulativeBrokerCommission)}
                </td>
                <td className="border-l-2 border-border-light px-space-3 py-space-2 text-right tabular-nums text-gray-mid">
                  {formatMoney(row.noteOutstandingPrincipal)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* The fourth part of the figure-clash mitigation, and the only one that
          says WHY out loud. The seller's principal column describes the receipt
          at the SELLER's rate; the note's balance runs at the buyer's contract
          rate. Both are correct and they do not agree. */}
      <p className="m-0 text-caption text-gray-mid" data-testid="commission-model-schedule-note">
        The seller&rsquo;s principal column describes how each receipt is composed
        at the seller&rsquo;s rate. The note&rsquo;s outstanding principal runs at
        the buyer&rsquo;s contract rate, so the two do not track each other.
      </p>
    </section>
  );
}

/** A filename slug for the downloaded report. Mirrors the server's own — which
 *  is authoritative via Content-Disposition; this only names the local save when
 *  a browser prefers the anchor's `download` attribute. */
function reportFilename(model: CommissionModelDto): string {
  const slug = (model.clientName ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return slug ? `${slug}-commission-report.pdf` : "commission-report.pdf";
}

/** Blob → object URL → anchor click. The StatementDownloadBar pattern, written
 *  out locally rather than reused: that component is a MONTH PICKER, and this
 *  report is about one scenario rather than one period — there is no month to
 *  choose, so reusing it would mean showing a control that changes nothing. */
function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface CommissionModelWorkupProps {
  model: CommissionModelDto;
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
  deleting?: boolean;
}

/** The full workup for one saved model. */
export function CommissionModelWorkup({
  model,
  onBack,
  onEdit,
  onDelete,
  deleting,
}: CommissionModelWorkupProps) {
  const { result } = model;

  // The report is generated on demand (no persisted artifact), so a slow render
  // needs a visible pending state and a failure needs somewhere to say so.
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await commissionModelerApi.report(model.id);
      triggerDownload(blob, reportFilename(model));
    } catch (err) {
      setDownloadError(errorMessage(err, "Couldn't generate the report."));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex flex-col gap-space-5" data-testid="commission-model-workup">
      <button
        type="button"
        onClick={onBack}
        data-testid="commission-model-back"
        className="self-start text-small font-medium text-gold hover:underline"
      >
        ← Back to all models
      </button>

      <header className="flex flex-wrap items-start justify-between gap-space-3">
        <div className="min-w-0">
          <h2 className="m-0 font-display text-h3 font-bold text-onyx">{model.name}</h2>
          <p className="m-0 mt-space-1 text-small text-gray-mid">
            {model.clientName ? `${model.clientName} · ` : ""}
            {monthlyFeeDisplay(model.monthlyFeeMode, model.monthlyFeeAmount)}
            {" · saved "}
            {formatDateInTz(model.modifiedAt ?? model.createdAt)}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-space-2">
          <button
            type="button"
            onClick={() => void handleDownload()}
            disabled={downloading}
            data-testid="commission-model-download"
            className="text-small font-medium text-gold hover:underline disabled:opacity-60"
          >
            {downloading ? "Preparing…" : "Download report"}
          </button>
          <button
            type="button"
            onClick={onEdit}
            data-testid="commission-model-edit"
            className="text-small font-medium text-gold hover:underline"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            data-testid="commission-model-delete"
            className="text-small font-medium text-status-error hover:underline disabled:opacity-60"
          >
            {deleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </header>

      {downloadError && (
        <p className="m-0 text-small text-status-error" role="alert">
          {downloadError}
        </p>
      )}

      <HeadlineFigures
        result={result}
        cashAtClosingMode={model.cashAtClosingMode}
        cashAtClosingPercent={model.cashAtClosingPercent}
      />

      <div className="rounded-lg border border-border-light bg-white-pure p-space-4">
        <TermsPanel terms={result.terms} />
      </div>

      <ScheduleTable result={result} />
    </div>
  );
}
